/**
 * Driver Price Notice — bulletin (not report).
 * Version 1.1 · Sprint 2 polish · Product Owner directive 2026-07-31.
 *
 * Goal: 30-second understanding on a phone screen.
 *   TODAY'S PRICE CHANGES
 *   ⬇ Regular Gas    -5.0¢
 *   ⬇ Premium        -5.0¢
 *   ⬇ Clear Diesel   -6.9¢
 *   Effective Today
 *
 * No table chrome. No footnotes. Big font. Minimal information.
 * URL: /driver-price-notice/:date  (standalone, outside the app layout).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowRight, Printer, Link as LinkIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const INTERNAL_PRODUCT_LABEL = {
  regular: "Regular Gas",
  premium: "Premium",
  clear_diesel: "Clear Diesel",
  dyed_diesel: "Dyed Diesel",
  def: "DEF",
};

export default function DriverPriceNotice() {
  const { date } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/price-notifications/driver-notice/${date}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError("No approved price notification for this date."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  const copyShareableLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Shareable link copied — paste into a driver chat or SMS.");
    } catch {
      toast.error("Could not copy link. Please copy the URL from your browser.");
    }
  };

  return (
    <div className="min-h-screen bg-surface px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-md" data-testid="driver-price-notice-page">
        {/* Action strip — hidden when printed */}
        <div className="mb-6 flex items-center justify-end gap-2 print:hidden">
          <button
            data-testid="dpn-copy-link"
            onClick={copyShareableLink}
            className="inline-flex items-center gap-1 rounded-md border border-divider bg-surface px-3 py-1.5 text-sm text-ink-primary hover:bg-surface-alt"
          >
            <LinkIcon size={14} /> Copy shareable link
          </button>
          <button
            data-testid="dpn-print"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1 rounded-md border border-divider bg-surface px-3 py-1.5 text-sm text-ink-primary hover:bg-surface-alt"
          >
            <Printer size={14} /> Print
          </button>
        </div>

        {loading && (
          <div className="text-center text-sm text-ink-secondary" data-testid="dpn-loading">
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="rounded-md border border-divider bg-surface p-6 text-center text-sm text-ink-secondary" data-testid="dpn-error">
            {error}
          </div>
        )}

        {data && !loading && (
          <>
            <h1
              data-testid="dpn-heading"
              className="mb-8 text-center font-display text-2xl font-bold uppercase tracking-wider text-ink-primary sm:text-3xl"
            >
              Today's Price Changes
            </h1>

            <ul className="space-y-6" data-testid="dpn-products">
              {(data.products || []).map((p) => {
                const label = INTERNAL_PRODUCT_LABEL[p.internal_product_code] || p.internal_product_code;
                const old = Number(p.old_price);
                const nw  = Number(p.new_price);
                const delta = Number.isFinite(old) && Number.isFinite(nw) ? nw - old : null;
                const deltaCents = delta === null ? null : delta * 100;
                const isDown = delta !== null && delta < 0;
                const isUp   = delta !== null && delta > 0;
                const tone   = isDown ? "text-emerald-600" : isUp ? "text-rose-600" : "text-ink-secondary";
                const Icon   = isDown ? ArrowDown : isUp ? ArrowUp : ArrowRight;
                return (
                  <li
                    key={p.internal_product_code}
                    className="flex items-baseline justify-between gap-4 border-b border-divider pb-4 last:border-b-0"
                    data-testid={`dpn-row-${p.internal_product_code}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={28} weight="bold" className={tone} />
                      <span className="font-display text-xl font-semibold text-ink-primary sm:text-2xl">
                        {label}
                      </span>
                    </div>
                    <span className={`font-display font-bold ${tone} text-2xl sm:text-3xl`}>
                      {delta === null
                        ? "—"
                        : `${isUp ? "+" : "-"}${Math.abs(delta).toFixed(4)}`}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div
              data-testid="dpn-effective"
              className="mt-10 text-center font-display text-lg uppercase tracking-wider text-ink-secondary"
            >
              Effective Today
            </div>
            <div className="mt-2 text-center text-[11px] text-ink-secondary">
              {formatDate(data.brief_date)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    // Bug fix (P0 · 2026-08-01): render the date in UTC so a viewer's
    // local timezone (Canada is west of UTC) can never subtract a day
    // from the effective date. The ISO string is the source of truth
    // — the approved Price Notification's effective_date. We never
    // derive the display date independently (no setDate(-1), no
    // subDays, no Morning Operations "yesterday" helper).
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}
